import { useMemo, useCallback } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import DataTable from '@components/shared/DataTable/DataTable';
import type {
  DataTableColumn,
  SortState,
} from '@components/shared/DataTable/DataTable';
import FilterPanel from '@components/shared/FilterPanel/FilterPanel';
import AppliedFiltersBar from '@components/shared/AppliedFiltersBar/AppliedFiltersBar';
import { getProductTypes } from '@/services/tenantService';
import { useTableFilters } from '@/hooks/useTableFilters';
import type { ProductTypeDto } from '@/types/tenant';
import type { FilterFieldConfig } from '@/types/filters';
import UsersEmptyIcon from '@assets/icons/UsersEmptyIcon';
import { validateTableSearch, buildSortString, toPaginationState } from '@utils/tabHelpers';
import { toTitleCase } from '@utils/textCase';
import type { TableSearchParams } from '@utils/tabHelpers';
import { useTranslation } from '@/hooks/useTranslation';
import { Forbidden } from '@workpay/ds-shared/components/Forbidden/Forbidden';
import { isForbiddenError } from '@workpay/ds-shared/components/Forbidden/ForbiddenBoundary';
import styles from './index.module.scss';

export const Route = createFileRoute('/w/product-types/')({
  validateSearch: validateTableSearch,
  component: ProductTypesPage,
});

// ============================================
// HELPERS
// ============================================

function StatusBadge({ status }: { status: ProductTypeDto['status'] }) {
  const className = status === 'ACTIVE' ? styles.badgeActive : styles.badgeInactive;
  return <span className={`${styles.badge} ${className}`}>{toTitleCase(status)}</span>;
}

// ============================================
// COMPONENT
// ============================================

function ProductTypesPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { page: urlPage, size: urlSize, sortBy, sortDir, filters: filtersParam } = Route.useSearch();

  const page = urlPage ?? 0;
  const size = urlSize ?? 10;

  const sort: SortState | undefined = useMemo(
    () => (sortBy && sortDir ? { columnId: sortBy, direction: sortDir } : undefined),
    [sortBy, sortDir],
  );

  // Filter fields ordered to match table columns:
  // Product Type, Description, Status
  const filterFields = useMemo<FilterFieldConfig[]>(
    () => [
      {
        fieldKey: 'productType',
        label: t('K_LABEL_PRODUCT_TYPE', 'Product Type'),
        type: 'text',
        placeholder: t('K_PLACEHOLDER_SEARCH_PRODUCT_TYPE', 'Search product type...'),
      },
      {
        fieldKey: 'productDesc',
        label: t('K_COLUMN_DESCRIPTION', 'Description'),
        type: 'text',
        placeholder: t('K_PLACEHOLDER_SEARCH_DESCRIPTION', 'Search description...'),
      },
      {
        fieldKey: 'status',
        label: t('K_FILTER_STATUS', 'Status'),
        type: 'select',
        options: [
          { label: toTitleCase(t('K_STATUS_ACTIVE', 'Active')), value: 'ACTIVE' },
          { label: toTitleCase(t('K_STATUS_INACTIVE', 'Inactive')), value: 'INACTIVE' },
        ],
      },
    ],
    [t],
  );

  const {
    filters,
    hasFilters,
    activeFilterCount,
    isPanelOpen,
    togglePanel,
    closePanel,
    applyFilters,
    removeFilter,
    clearAllFilters,
    filterPayload,
  } = useTableFilters({
    routeTo: '/w/product-types',
    filtersParam,
    fields: filterFields,
  });

  const columns = useMemo<DataTableColumn<ProductTypeDto>[]>(
    () => [
      {
        id: 'productType',
        header: t('K_LABEL_PRODUCT_TYPE', 'Product Type'),
        sortable: true,
        sortKey: 'productType',
        width: '200px',
        cell: (item) => (
          <span className={styles.cellPrimary} title={item.productType || '-'}>{item.productType || '-'}</span>
        ),
      },
      {
        id: 'productDesc',
        header: t('K_COLUMN_DESCRIPTION', 'Description'),
        cell: (item) => <span title={item.productDesc || '-'}>{item.productDesc || '-'}</span>,
      },
      {
        id: 'status',
        header: t('K_COLUMN_STATUS', 'Status'),
        sortable: true,
        sortKey: 'status',
        cell: (item) => <StatusBadge status={item.status} />,
      },
    ],
    [t],
  );

  const sortString = useMemo(() => buildSortString(sort, columns), [sort, columns]);

  const handleSortChange = useCallback(
    (newSort: SortState | undefined) => {
      void navigate({
        to: '/w/product-types',
        search: (prev: TableSearchParams) => ({
          ...prev,
          page: 0,
          sortBy: newSort?.columnId,
          sortDir: newSort?.direction,
        }),
        replace: true,
      });
    },
    [navigate],
  );

  const handlePageChange = useCallback(
    (newPage: number) => {
      void navigate({
        to: '/w/product-types',
        search: (prev: TableSearchParams) => ({ ...prev, page: newPage }),
        replace: true,
      });
    },
    [navigate],
  );

  const handlePageSizeChange = useCallback(
    (newSize: number) => {
      void navigate({
        to: '/w/product-types',
        search: (prev: TableSearchParams) => ({ ...prev, size: newSize, page: 0 }),
        replace: true,
      });
    },
    [navigate],
  );

  const { data, isLoading, isFetching, error } = useQuery({
    queryKey: ['productTypes', { page, size, sort: sortString, filterPayload }],
    queryFn: () => getProductTypes({ page, size, sort: sortString }, filterPayload),
  });

  const items = data?.content ?? [];
  const pagination = useMemo(() => toPaginationState(data), [data]);
  const listForbidden = isForbiddenError(error);

  const getRowKey = useCallback((item: ProductTypeDto) => item.id, []);

  const emptyState = useMemo(
    () =>
      listForbidden ? (
        <Forbidden variant="empty-state" hideAction />
      ) : (
        <div className={styles.emptyState}>
          <UsersEmptyIcon className={styles.emptyIcon} />
          <h2 className={styles.emptyTitle}>{t('K_EMPTY_NO_PRODUCT_TYPES', 'No product types found')}</h2>
          <p className={styles.emptyText}>
            {t('K_EMPTY_PRODUCT_TYPES_DESC', 'Product types will appear here once they are configured.')}
          </p>
        </div>
      ),
    [listForbidden, t],
  );

  return (
    <div className={styles.page}>
      {/* Page Header */}
      <div className={styles.header}>
        <div className={styles.headerInfo}>
          <h1 className={styles.title}>{t('K_LABEL_PRODUCT_TYPES', 'Product Types')}</h1>
          <p className={styles.subtitle}>{t('K_PAGE_SUBTITLE_PRODUCT_TYPES', 'View product type configurations')}</p>
        </div>
      </div>

      {hasFilters && (
        <AppliedFiltersBar
          fields={filterFields}
          values={filters}
          onRemove={removeFilter}
          onClearAll={clearAllFilters}
        />
      )}

      <DataTable
        columns={columns}
        data={items}
        rowKey={getRowKey}
        loading={isLoading}
        fetching={isFetching}
        loadingText={t('K_LOADING_PRODUCT_TYPES', 'Loading product types...')}
        emptyState={emptyState}
        sort={sort}
        onSortChange={handleSortChange}
        pagination={pagination}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        showToolbar
        onFilterClick={togglePanel}
        activeFilterCount={activeFilterCount}
        aria-label="Product types list"
      />

      <FilterPanel
        isOpen={isPanelOpen}
        onClose={closePanel}
        fields={filterFields}
        values={filters}
        onApply={applyFilters}
        onReset={clearAllFilters}
        title={t('K_FILTER_TITLE', 'Filters')}
      />
    </div>
  );
}
